import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import type { AppointmentFormValues } from '@/types/appointment-form';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function startOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function getStripDays(anchor: Date) {
  return Array.from({ length: 5 }, (_, i) => {
    const d = startOfDay(anchor);
    d.setDate(anchor.getDate() + i);
    return d;
  });
}

function isSameDay(a: Date, b: Date) {
  return (
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear()
  );
}

/**
 * DatePickerSection — tappable "MMM dd, yyyy ▼" header + 5-day strip.
 * Mirrors Flutter no-provider-state date picker (create_appointment_widget.dart:816–911).
 * Slot lookup lives in TimeSlotsSection (post-profile).
 */
export function DatePickerSection() {
  const { watch, setValue } = useFormContext<AppointmentFormValues>();
  const selectedDate = watch('selectedDate');
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (!selectedDate) {
      setValue('selectedDate', startOfDay(new Date()).toISOString(), { shouldValidate: true });
    }
  }, [selectedDate, setValue]);

  const today = startOfDay(new Date());
  const selectedDateObj = selectedDate ? new Date(selectedDate) : today;

  const [anchorDate, setAnchorDate] = useState(() => startOfDay(new Date()));
  const stripDays = getStripDays(anchorDate);

  // Re-anchor strip when selected date moves outside the visible window.
  useEffect(() => {
    if (!selectedDate) return;
    const sd = startOfDay(new Date(selectedDate));
    setAnchorDate((prev) => {
      const prevEnd = new Date(prev);
      prevEnd.setDate(prev.getDate() + 4);
      const startsBefore = sd.getTime() < prev.getTime();
      const endsAfter = sd.getTime() > prevEnd.getTime();
      return startsBefore || endsAfter ? sd : prev;
    });
  }, [selectedDate]);

  // Today is the floor — past dates aren't bookable. Flutter
  // (`create_appointment_widget.dart:828`) ships `firstDate =
  // DateTime.now().subtract(Duration(days: 1))` (yesterday) which
  // lets the user pick a past date by accident; we tighten to today.
  const minDate = startOfDay(new Date());

  const selectDay = (d: Date) => {
    if (startOfDay(d).getTime() < minDate.getTime()) return;
    setValue('selectedDate', startOfDay(d).toISOString(), { shouldValidate: true });
    setValue('slotId', null);
    setValue('slotStart', null);
    setValue('slotEnd', null);
  };

  const shiftStrip = (deltaDays: number) => {
    const d = new Date(anchorDate);
    d.setDate(d.getDate() + deltaDays);
    if (d.getTime() < minDate.getTime()) return;
    setAnchorDate(d);
  };

  return (
    <div className="px-4 py-3 border-t border-border-subtle">
      <div className="flex items-center justify-between mb-2">
        <button type="button" onClick={() => shiftStrip(-5)} className="p-1 hover:bg-grey-100 rounded" aria-label="Previous days">
          <ChevronLeft className="h-4 w-4 text-text-muted" />
        </button>
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger
            render={
              <button
                type="button"
                className="inline-flex items-center gap-1 text-sm font-semibold text-text-primary hover:text-brand-primary"
              >
                <span>
                  {MONTH_LONG[selectedDateObj.getMonth()]} {String(selectedDateObj.getDate()).padStart(2, '0')}, {selectedDateObj.getFullYear()}
                </span>
                <ChevronDown className="h-4 w-4" />
              </button>
            }
          />
          <PopoverContent align="center" className="w-auto p-2">
            <Calendar
              mode="single"
              selected={selectedDateObj}
              defaultMonth={selectedDateObj}
              onSelect={(d) => {
                if (d) {
                  selectDay(d);
                  setPickerOpen(false);
                }
              }}
              disabled={{ before: minDate }}
              showOutsideDays
              fixedWeeks
            />
            {/* Footer matches the Signal-DS DatePicker: blue "Today" link + value. */}
            <div className="mt-2 flex items-center justify-between border-t border-border-default pt-2 text-size-sm">
              <button
                type="button"
                onClick={() => {
                  selectDay(new Date());
                  setPickerOpen(false);
                }}
                className="font-medium text-brand-primary hover:underline"
              >
                Today
              </button>
              <span className="text-text-muted">
                {MONTH_LONG[selectedDateObj.getMonth()]} {String(selectedDateObj.getDate()).padStart(2, '0')}, {selectedDateObj.getFullYear()}
              </span>
            </div>
          </PopoverContent>
        </Popover>
        <button type="button" onClick={() => shiftStrip(5)} className="p-1 hover:bg-grey-100 rounded" aria-label="Next days">
          <ChevronRight className="h-4 w-4 text-text-muted" />
        </button>
      </div>

      <div className="grid grid-cols-5 gap-1">
        {stripDays.map((d) => {
          const isSelected = isSameDay(d, selectedDateObj);
          const isPast = d.getTime() < minDate.getTime();
          return (
            <button
              key={d.toISOString()}
              type="button"
              disabled={isPast}
              onClick={() => selectDay(d)}
              className={cn(
                'flex flex-col items-center py-2 rounded-xl border transition-colors',
                isSelected
                  ? 'bg-brand-primary border-brand-primary text-white'
                  : isPast
                    ? 'border-border-default text-text-hint cursor-not-allowed opacity-50'
                    : 'border-border-default text-text-muted hover:border-brand-primary hover:text-text-primary',
              )}
            >
              <span className="text-xs font-medium">{MONTH_SHORT[d.getMonth()]}</span>
              <span className="text-md font-bold leading-tight">{d.getDate()}</span>
              <span className="text-xs font-medium">{DAY_NAMES[d.getDay()]}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
